import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Input, InputNumber, Row, Select, Space, Statistic, Table, Tag, Typography, message } from 'antd';
import { ReloadOutlined, WalletOutlined } from '@ant-design/icons';
import { BrandService } from '../services/brandService';
import CreditService, { CreditBalance, CreditTransaction } from '../services/creditService';
import { Brand } from '../types';

const { Title, Text } = Typography;
const DEFAULT_REASON = 'manual_top_up';

const AdminCreditManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrandId, setSelectedBrandId] = useState<string>();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedBrand = useMemo(
    () => brands.find((brand) => brand.id === selectedBrandId) || null,
    [brands, selectedBrandId]
  );

  const loadBrands = useCallback(async () => {
    try {
      setLoadingBrands(true);
      const brandList = await BrandService.getAllBrands();
      setBrands(brandList);
      if (!selectedBrandId && brandList.length > 0) {
        setSelectedBrandId(brandList[0].id);
      }
    } catch (error) {
      message.error('Markalar yüklenemedi');
    } finally {
      setLoadingBrands(false);
    }
  }, [selectedBrandId]);

  const loadCredits = async (brandId: string) => {
    try {
      setLoadingCredits(true);
      const [balanceData, transactionData] = await Promise.all([
        CreditService.getBalance(brandId),
        CreditService.getTransactions(brandId),
      ]);
      setBalance(balanceData);
      setTransactions(transactionData);
    } catch (error: any) {
      setBalance(null);
      setTransactions([]);
      message.error(error.message || 'Kredi verileri yüklenemedi');
    } finally {
      setLoadingCredits(false);
    }
  };

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  useEffect(() => {
    if (selectedBrandId) {
      loadCredits(selectedBrandId);
    }
  }, [selectedBrandId]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedBrandId) {
        message.warning('Önce bir marka seçin');
        return;
      }

      setSubmitting(true);
      const nextBalance = await CreditService.adminAdjustCredits(selectedBrandId, {
        amount: values.amount,
        reason: values.reason || DEFAULT_REASON,
      });
      setBalance(nextBalance);
      message.success(`${values.amount} kredi tanımlandı`);
      form.resetFields();
      form.setFieldsValue({ reason: DEFAULT_REASON });
      await loadCredits(selectedBrandId);
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error.message || 'Kredi tanımlanamadı');
    } finally {
      setSubmitting(false);
    }
  };

  const transactionColumns = [
    {
      title: 'Tarih',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) => new Date(value).toLocaleString('tr-TR'),
    },
    {
      title: 'Tip',
      dataIndex: 'type',
      key: 'type',
      render: (value: string) => <Tag color={value === 'adjustment' ? 'blue' : 'default'}>{value}</Tag>,
    },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      key: 'amount',
      render: (value: number) => <Text strong>{value > 0 ? `+${value}` : value} kredi</Text>,
    },
    {
      title: 'Bakiye Sonrası',
      dataIndex: 'balanceAfter',
      key: 'balanceAfter',
      render: (value: number) => `${value} kredi`,
    },
    {
      title: 'Referans',
      dataIndex: 'referenceType',
      key: 'referenceType',
      render: (value?: string) => value || '-',
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Admin Kredi Tanımlama</Title>
        <Text type="secondary">
          Admin olarak istediğin markaya kredi ekle. Bu işlem `brand_credit_balances` bakiyesini artırır ve
          `brand_credit_transactions` tablosuna `admin_adjustment` ledger kaydı yazar.
        </Text>
      </div>

      <Alert
        style={{ marginBottom: 24 }}
        type="info"
        showIcon
        message="Endpoint kontratı"
        description="POST /api/v1/admin/brands/:brandId/credits/adjust → body: { amount, reason } → response: { success, balance }"
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={10}>
          <Card title="Kredi tanımla" extra={<Button icon={<ReloadOutlined />} onClick={loadBrands} loading={loadingBrands}>Markaları yenile</Button>}>
            <Form form={form} layout="vertical" initialValues={{ reason: DEFAULT_REASON }}>
              <Form.Item label="Marka" required>
                <Select
                  showSearch
                  placeholder="Marka seç"
                  loading={loadingBrands}
                  value={selectedBrandId}
                  onChange={setSelectedBrandId}
                  optionFilterProp="label"
                  options={brands.map((brand) => ({ value: brand.id, label: `${brand.name} · ${brand.id}` }))}
                />
              </Form.Item>

              <Form.Item label="Kredi miktarı" name="amount" rules={[{ required: true, message: 'Kredi miktarı zorunlu' }]}> 
                <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="100" />
              </Form.Item>

              <Form.Item label="Sebep" name="reason">
                <Input placeholder="manual_top_up" />
              </Form.Item>

              <Space>
                <Button type="primary" onClick={handleSubmit} loading={submitting} icon={<WalletOutlined />}>
                  Krediyi tanımla
                </Button>
                <Button onClick={() => selectedBrandId && loadCredits(selectedBrandId)} loading={loadingCredits}>
                  Bakiyeyi yenile
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Card title="Seçili marka özeti" loading={loadingCredits}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div>
                <Text strong>{selectedBrand?.name || 'Marka seçilmedi'}</Text>
                <br />
                <Text type="secondary">{selectedBrandId || '—'}</Text>
              </div>
              <Row gutter={16}>
                <Col span={8}><Statistic title="Kullanılabilir" value={balance?.availableCredits || 0} suffix="kredi" /></Col>
                <Col span={8}><Statistic title="Rezerve" value={balance?.reservedCredits || 0} suffix="kredi" /></Col>
                <Col span={8}><Statistic title="Toplam harcanan" value={balance?.lifetimeSpentCredits || 0} suffix="kredi" /></Col>
              </Row>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card title="Son kredi hareketleri" style={{ marginTop: 24 }} loading={loadingCredits}>
        <Table rowKey="id" columns={transactionColumns} dataSource={transactions} pagination={{ pageSize: 10 }} />
      </Card>
    </div>
  );
}

export default AdminCreditManagement;
